import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Inject,
  AfterViewInit,
  ViewContainerRef,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { LocationInterface } from '../../shared/interfaces/location-interface';
import { forkJoin, startWith } from 'rxjs';
import { SkihirePoiInterface } from '../../shared/interfaces/skihire-poi-interface';
import { NotificationService } from '../../shared/services/notification.service';
import { ShopService, eventTimeType } from '../../shared/services/shop.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { TagManagerService } from '../../core/services/tag-manager.service';
import { TownService } from '../../core/services/town.service';
import { Meta, Title } from '@angular/platform-browser';
import { LocalizeRouterModule, LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { NavigationService } from '../../core/services/navigation.service';
import { ResolutionService } from '../../core/services/resolution.service';
import { SurrogateKeyService } from '../../core/services/surrogate-key.service';
import { ResponseService } from '../../core/services/response.service';
import { BROWSER_WINDOW } from '../../core/injection-tokens/browser-window';
import { PartnerLinkService } from '../../core/services/partner-link.service';
import { ShopDetailService } from '../../shared/services/shop-detail.service';
import { ShopListInterface } from '../../shared/interfaces/shop-list-interface';
import { SHOPLIST_ANIMATIONS } from '../../core/animations/SHOPLIST_ANIMATIONS';
import { ShopSortingService } from '../../shared/services/shop-sorting.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { BookingParameterService } from '../../core/services/booking-parameter.service';
import { ScrollService } from '../../core/services/scroll.service';
import { ShopSearchHelperService } from '../../shared/landing-page/search/shop-search-helper.service';
import { Shop, unbookableType } from '../../shared/entities/shop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BookingParametersInterface,
  codetype,
  copyBookingParameters
} from '../../shared/entities/bookingParameters';
import { Town } from '../../shared/entities/town';
import { TownNotFoundInterface } from '../../shared/interfaces/town-not-found-interface';
import { ShopSearchComponent } from '../../shared/landing-page/search/shop-search/shop-search.component';
import { DeferLoadModule } from '@trademe/ng-defer-load';
import { IconComponent, info, provideIcons } from '../../shared/icons';
import { ShopComponent } from '../../shared/saleslocation/shop/shop.component';
import { ShopSortComponent } from '../../shared/saleslocation/shop-sort/shop-sort.component';
import { ShopFilterComponent } from '../../shared/saleslocation/filter/shop-filter.component';
import { ShopFilterService } from '../../core/services/shop-filter.service';
import { AlpitranslatePipe } from '../../core/pipes/alpitranslate.pipe';
import { MapComponent } from '@shared/components';
import { MapService } from '../../shared/services/map.service';
import { CrowdinTranslationService } from '../../shared/services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';

/**
 * used to list shops like in town but for points of interest.
 * Has a unique enough markup and style to be a separate Component
 */
@Component({
  selector: 'app-poi',
  templateUrl: './poi.component.html',
  styleUrls: ['./poi.component.scss'],
  animations: SHOPLIST_ANIMATIONS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ info })],
  imports: [
    MapComponent,
    DeferLoadModule,
    RouterModule,
    LocalizeRouterModule,
    IconComponent,
    ShopComponent,
    ShopSortComponent,
    ShopFilterComponent,
    CommonModule,
    ShopSearchComponent,
    AlpitranslatePipe
  ]
})
export class PoiComponent implements OnInit, AfterViewInit {
  @ViewChild('map', { static: true }) map: ElementRef;
  @ViewChild('rating', { read: ViewContainerRef }) protected rating: ViewContainerRef;
  @ViewChild('search', { static: true }) protected search: ShopSearchComponent;

  towns = new Array<{ name: string; townSlugs: { [key: string]: string } }>();

  // the current displayed bookingParameters info. will be updated by events from the search form
  protected bookingParameters: BookingParametersInterface;
  // display loading indicator or not. Loading for shops
  protected loading = false;
  // false on init, will be set to true if apiService got an error response.
  protected error = false;
  // date until alpinflexi is offered for free!
  protected freeFlexiUntil: Date;
  protected showFreeFlexi = false;
  // the shops loaded with the apiService (see updateShops subscription)
  protected shops: Array<Shop>;
  // the town loaded with the townService
  protected town: Town;
  // the current language
  protected culture: string;
  protected isBrowser = this.platformInformationService.getIsBrowser();

  protected showAdditionalElements = false;
  protected displayDesktopElements = false;
  // determines how many shops to lazyLoad in or skip lazy loading after sorting or townchange
  protected numberOfInitialShow = 0;
  protected skipDeferLoading: boolean;

  protected saleslocationNotFound: TownNotFoundInterface;
  protected saleslocationEmpty = false; // used when shops are loaded but api was empty (no shops in saleslocation)

  protected hotlines: { [key: string]: string };
  protected hotline: string;

  // if the redirect contains invalidDate query infos
  private invalidDate = false;
  // if the redirect contains invalidAge query infos
  private invalidAge = false;
  // the invalid shop id from the redirect query
  private invalidShopId: number;

  constructor(
    protected shopFilterService: ShopFilterService,
    protected notificationService: NotificationService,
    protected shopService: ShopService,
    protected route: ActivatedRoute,
    public platformInformationService: PlatformInformationService,
    public partnerLinkService: PartnerLinkService,
    protected datePipe: DatePipe,
    protected translateService: TranslateService,
    protected tagManager: TagManagerService,
    protected mapService: MapService,
    protected townService: TownService,
    protected title: Title,
    protected meta: Meta,
    protected localizedRouterService: LocalizeRouterService,
    protected router: Router,
    protected navigationService: NavigationService,
    protected resolutionService: ResolutionService,
    protected surrogateKeyService: SurrogateKeyService,
    protected responseService: ResponseService,
    @Inject(BROWSER_WINDOW) private window,
    private shopDetailService: ShopDetailService,
    private analyticsService: AnalyticsService,
    public shopSortingService: ShopSortingService,
    private bookingParameterService: BookingParameterService,
    private scrollService: ScrollService,
    protected shopSearchHelperService: ShopSearchHelperService,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef,
    private crowdinTranslationService: CrowdinTranslationService
  ) {}

  ngOnInit() {
    this.crowdinTranslationService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => this.updateTitle()
    });
    this.updateTitle();
    // shopservice has already loaded shops? use them
    this.shops = this.shopService.currentShops;
    if (this.shops.length) {
      this.loading = false;
    } else {
      this.loading = true;
    }
    this.bookingParameters = this.bookingParameterService.getBookingParameters();
    if (!this.bookingParameters.ages || !this.bookingParameters.ages.length) {
      this.bookingParameters.ages = [36];
    }

    this.resolutionService.sizeReferenceChange
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(this.resolutionService.getCurrentSizeReference()))
      .subscribe(currentSizeReference => {
        this.showAdditionalElements = currentSizeReference > this.resolutionService.extraSmallReference;
        this.displayDesktopElements = currentSizeReference > this.resolutionService.mediumReference;
        this.cdr.detectChanges();
      });

    this.shopDetailService.showOnMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.openMapForShop());
    this.shopDetailService.displayingShop
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onDisplayingShop.bind(this));
    this.townService.salesLocationNotFound
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onSaleslocationNotFoundUpdate.bind(this));
    this.localizedRouterService.routerEvents
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onLanguageChange.bind(this));

    // errors on shop loading
    this.shopService.loadingError
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onErrorUpdate.bind(this));

    this.shopService.updates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      switch (event.type) {
        case eventTimeType.preShopUpdate:
          this.preShopUpdate(event.data.bookingParameters);
          break;
        case eventTimeType.onShopUpdate:
          this.onShopUpdate(event.data);
          break;
        case eventTimeType.onInTown:
          this.onOfferUpdate(event.data);
          break;
        case eventTimeType.onOutOfTown:
          this.onOutOfTownOfferUpdate(event.data);
      }
    });

    const queryParams = this.route.snapshot.queryParamMap;
    // got rerouted from page because shop is not available!
    if (queryParams.get('invalidDates') && queryParams.get('shopId')) {
      this.invalidDate = true;
      this.invalidShopId = +queryParams.get('shopId');
      this.router.navigate([], {
        queryParams: { invalidDates: null, shopId: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
    // got rerouted from page because shop is not available!
    if (queryParams.get('invalidAges') && queryParams.get('shopId')) {
      this.invalidAge = true;
      this.invalidShopId = +queryParams.get('shopId');
      this.router.navigate([], {
        queryParams: { invalidAges: null, shopId: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    this.platformInformationService.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.freeFlexiUntil = new Date(data.freeFlexiUntil);
      this.freeFlexiUntil.setDate(this.freeFlexiUntil.getDate() - 1);
      this.showFreeFlexi = new Date(data.freeFlexiUntil) >= new Date();
      this.hotlines = data.hotlines;
      this.hotline = this.hotlines[this.translateService.currentLang];
    });

    // will be 'AT' on SSR, see getCustomerCountryCode() in platformservice
    this.platformInformationService.customerCountryCode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customerCountryCode: string) => {
        if (customerCountryCode === 'FR') {
          this.hotline = this.hotline.replace('+33 ', '0');
        }
      });

    this.shopDetailService.goToProduct
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onGoToProduct.bind(this));

    this.mapService.shopChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((shop: Shop) => {
      this.mapService.closeMap();
      this.scrollToSelectedShop(shop);
    });

    // get townslug and prepare all services that will use it
    // this.routerParamSub = this.route.paramMap.subscribe(params => {
    const params = this.route.snapshot.paramMap;
    if (params.has('slug')) {
      // set the townslug according to current url
      this.bookingParameters.slug = params.get('slug');
      // "fake" the poi we're currently on to enable correct <- landing page -> town page switch without search page
      this.bookingParameters.poi = {
        type: 'PointOfInterest',
        url: '/de/skiverleih/' + this.bookingParameters.slug
      };
      // prepare the shop service to load shops and discount ticker
      this.shopService.updateBookingParameters(this.bookingParameters);
      // update town info and trigger updates on depending translations and texts
      this.townService.onTownUpdate.subscribe(town => {
        this.town = town;
        this.updateTitle();
      });
      this.townService.getTownForSlug(this.bookingParameters.slug);
    }

    forkJoin([this.shopService.shops$, this.townService.town$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.town = data[1];
        this.updateTitle();
        this.mapService.centerMapOnShops(data[0]);
        if (this.town.relatedSaleslocations.length) {
          this.towns = this.town.relatedSaleslocations;
        } else {
          this.towns = new Array<{ name: string; townSlugs: { [key: string]: string } }>();
          data[0].forEach(shop => {
            if (
              !this.towns.find(_town => _town.name === shop.townName) &&
              this.distanceBetweenLocations(this.town.location, shop.location) <= 12
            ) {
              this.towns.push({ name: shop.townName, townSlugs: shop.townSlugs });
            }
          });
          this.towns.sort((n1, n2) => {
            if (n1.name < n2.name) {
              return -1;
            }
            if (n1.name > n2.name) {
              return 1;
            }
            return 0;
          });
        }
        this.surrogateKeyService.addSurrogateKeys(['poi-' + data[1].id]);
      });
  }

  ngAfterViewInit() {
    if (this.shops) {
      // recenters map on town when user klicks on the "back" button in the browser
      this.mapService.centerMapOnShops(this.shops);
    }
  }

  private preShopUpdate(bookingParameters: BookingParametersInterface) {
    this.bookingParameters = bookingParameters;
    this.loading = true;
    this.saleslocationEmpty = false;
    this.error = false;
    this.saleslocationNotFound = null;
  }

  private onShopUpdate(response: { bookingParameters: BookingParametersInterface; shops: Array<Shop> }) {
    this.loading = false;
    if (!response.shops.length) {
      // no shops in saleslocation
      this.error = true;
      this.saleslocationEmpty = true;
      this.responseService.setResponseCode(404);
    }
    this.bookingParameters = response.bookingParameters;
    this.shops = response.shops;
    this.onUnbookableUpdate();
  }

  private onOfferUpdate(response: { bookingParameters: BookingParametersInterface; shops: Array<Shop> }) {
    this.bookingParameters = response.bookingParameters;
    this.shops = response.shops;
    // when sorting by price, we need to wait until out of town offers are ready
    if (this.shopSortingService.getCurrentSelection() !== 'price') {
      this.loading = false;
      this.sortShops(this.shopSortingService.getCurrentSelection());
      this.tagManager.push({ event: 'optimize.activate' });
    }
  }

  private onOutOfTownOfferUpdate(response: {
    shops: Array<Shop>;
    bookingParameters: BookingParametersInterface;
  }) {
    // loaded offers for out of town shops.
    if (response.shops.length > 0) {
      this.bookingParameters = response.bookingParameters;
      this.shops = response.shops;
      this.loading = false;
      this.sortShops(this.shopSortingService.getCurrentSelection());
    }
    if (this.shopSortingService.getCurrentSelection() === 'price') {
      this.tagManager.push({ event: 'optimize.activate' });
    }
  }

  onUnbookableUpdate() {
    if (this.invalidDate || this.invalidAge) {
      const invalidShop = this.shops.find((predicate: Shop) => {
        if (+predicate.id === this.invalidShopId) {
          return true;
        }
      });
      const unbookableReasons = invalidShop?.unbookableReasonsFor(
        this.bookingParameters.start,
        this.bookingParameters.end,
        this.bookingParameters.ages
      );
      if (invalidShop && unbookableReasons.length && this.invalidDate) {
        switch (unbookableReasons[0].type) {
          case unbookableType.minDuration:
            this.notificationService.add({
              displayTime: 0,
              transkey: 'POI.shopMinDuration',
              transvars: {
                shop_name: invalidShop.name,
                shop_address: invalidShop.address,
                min_duration: unbookableReasons[0].minDuration
              },
              id: 'blocker'
            });
            this.createAnalyticsEvent(
              'Error Message',
              'The minimum booking duration in this store is {{ min_duration }} day(s).'
            );
            this.invalidDate = false;
            break;
          case unbookableType.maxDuration:
            this.notificationService.add({
              displayTime: 0,
              transkey: 'POI.shopMaxDuration',
              transvars: {
                shop_name: invalidShop.name,
                shop_address: invalidShop.address,
                max_duration: unbookableReasons[0].maxDuration
              },
              id: 'blocker'
            });
            this.createAnalyticsEvent(
              'Error Message',
              'The maximum booking duration in this store is {{ max_duration }} days.'
            );
            this.invalidDate = false;
            break;
          case unbookableType.minDelay:
            this.notificationService.add({
              displayTime: 0,
              transkey: 'POI.shopMinDelay',
              transvars: {
                shop_name: invalidShop.name,
                shop_address: invalidShop.address,
                min_delay: unbookableReasons[0].minDelay
              },
              id: 'blocker'
            });
            this.createAnalyticsEvent(
              'Error Message',
              'We are sorry, This shop can not accept any bookings at short notice.'
            );
            this.invalidDate = false;
            break;
          case unbookableType.after:
            this.translateService.get('POI.offerValidDateFormat').subscribe((dateFormat: string) => {
              this.notificationService.add({
                displayTime: 0,
                transkey: 'POI.shopAfterSeason',
                transvars: {
                  shop_name: invalidShop.name,
                  shop_address: invalidShop.address,
                  end_date: this.datePipe.transform(unbookableReasons[0].end, dateFormat, undefined, 'en-US')
                },
                id: 'blocker'
              });
              this.createAnalyticsEvent(
                'Error Message',
                'The shop will be closed after {{ end_date }}. Please choose another period'
              );
            });
            this.invalidDate = false;
            break;
          case unbookableType.before:
            this.translateService.get('POI.shopBeforeSeasonDateFormat').subscribe((dateFormat: string) => {
              this.notificationService.add({
                displayTime: 0,
                transkey: 'POI.shopBeforeSeason',
                transvars: {
                  shop_name: invalidShop.name,
                  shop_address: invalidShop.address,
                  start_date: this.datePipe.transform(
                    unbookableReasons[0].start,
                    dateFormat,
                    undefined,
                    'en-US'
                  )
                },
                id: 'blocker'
              });
              this.createAnalyticsEvent(
                'Error Message',
                'The shop is only open from the {{ start_date }}. Please choose another period'
              );
            });
            this.invalidDate = false;
            break;
          case unbookableType.pause:
            this.translateService.get('POI.offerValidDateFormat').subscribe((dateFormat: string) => {
              this.notificationService.add({
                displayTime: 0,
                transkey: 'POI.shopPause',
                transvars: {
                  shop_name: invalidShop.name,
                  shop_address: invalidShop.address,
                  start_date: this.datePipe.transform(
                    unbookableReasons[0].start,
                    dateFormat,
                    undefined,
                    'en-US'
                  ),
                  end_date: this.datePipe.transform(unbookableReasons[0].end, dateFormat, undefined, 'en-US')
                },
                id: 'blocker'
              });
              this.createAnalyticsEvent(
                'Error Message',
                'Out of stock for the period {{ start_date }} - {{ end_date }}. Please choose another period'
              );
            });
            this.invalidDate = false;
            break;
        }
      } else if (invalidShop && unbookableReasons.length && this.invalidAge) {
        if (unbookableReasons[0].type === unbookableType.unservableAges) {
          this.notificationService.add({
            displayTime: 0,
            transkey: 'POI.shopUnservableAges',
            transvars: {
              shop_name: invalidShop.name,
              shop_address: invalidShop.address,
              age: unbookableReasons[0].age
            },
            id: 'blocker'
          });
          this.createAnalyticsEvent('Error Message', 'The ages can not be served by the shop.');
          this.invalidAge = false;
        }
      }
    }
  }

  /**
   * updates offers with given array<number> of ages
   * @param ages
   */
  updatePersons(ages: Array<number>) {
    if (this.supportInteractiveMode()) {
      const data = copyBookingParameters(this.bookingParameters);
      data.ages = ages;
      this.shopService.updateBookingParameters(data);
    }
  }

  /**
   * updates offers with given promocode
   * @param code new valid promocode
   */
  updatePromocode(code: string) {
    if (this.supportInteractiveMode()) {
      const data = copyBookingParameters(this.bookingParameters);
      data.setActioncode({ type: codetype.promo, text: code });
      this.shopService.updateBookingParameters(data);
    }
  }

  /**
   * update offers and unbookable info depending on date range
   * @param range Array of start and end date
   */
  updateTimerange(range: { start: Date; end: Date }) {
    if (this.supportInteractiveMode()) {
      const data = copyBookingParameters(this.bookingParameters);
      data.start = range.start;
      data.end = range.end;
      this.shopService.updateBookingParameters(data);
    }
  }

  updateForm(event: BookingParametersInterface, forceReload?: boolean) {
    // switched town, we cant scroll anymore so remove last selected shop
    if (event.slug.localeCompare(this.bookingParameters.slug) !== 0) {
      this.shopSearchHelperService.lastSelectedShop = null;
    }
    this.bookingParameters.userDefinedSearch = event.userDefinedSearch;
    this.shopService.updateBookingParameters(event, forceReload);
    if (this.shopSearchHelperService.lastSelectedShop) {
      this.scrollToSelectedShop(this.shopSearchHelperService.lastSelectedShop);
      this.shopSearchHelperService.lastSelectedShop = null;
    }

    if (event.poi) {
      if (this.bookingParameters.partner) {
        this.navigationService.navigateToPoi(
          event.poi,
          false,
          this.bookingParameters.partner.replace('partner-', 'partner-/')
        );
      } else {
        this.navigationService.navigateToPoi(event.poi);
      }
    }
  }

  protected async initRatings() {
    const { TrustpilotComponent } = await import('../../shared/components/trustpilot/trustpilot.component');
    this.rating.clear();
    this.rating.createComponent(TrustpilotComponent);
  }

  protected retryLoad() {
    this.error = false;
    this.shopService.updateBookingParameters(this.bookingParameters, true);
  }

  /**
   * sorts shops according to given sorting type (price, distance, price_distance)
   * @param type sorting type
   * @param displayRoedler if the sort loading indicator should be displayed or not
   */
  protected sortShops(type: string) {
    this.shops = this.shopSortingService.sortShops(type, this.shops);
    return this.shops;
  }

  /**
   * generally used geo measurement function
   * https://en.wikipedia.org/wiki/Haversine_formula
   * @param location from
   * @param location to
   */
  private distanceBetweenLocations(location1: LocationInterface, location2: LocationInterface): number {
    const R = 6378.137; // Radius of earth in KM
    const dLat = (location2.latitude * Math.PI) / 180 - (location1.latitude * Math.PI) / 180;
    const dLon = (location2.longitude * Math.PI) / 180 - (location2.longitude * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((location1.latitude * Math.PI) / 180) *
        Math.cos((location2.latitude * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    return d;
  }

  updateLocation(event: { slug: string; poi: SkihirePoiInterface }) {
    this.skipDeferLoading = true;
    if (this.supportInteractiveMode()) {
      let reload = false;
      if (this.saleslocationNotFound) {
        this.saleslocationNotFound = null;
        reload = true;
        this.error = false;
      }
      const data = copyBookingParameters(this.bookingParameters);
      data.slug = event.slug;
      data.poi = event.poi;
      this.shopService.updateBookingParameters(data, reload);
      if (data.partner) {
        this.navigationService.navigateToPoi(event.poi, false, data.partner.replace('partner-', 'partner-/'));
      } else {
        this.navigationService.navigateToPoi(event.poi);
      }
    } else if (event.poi.type === 'AlpinShop') {
      if (this.bookingParameters.partner) {
        this.navigationService.navigateToPoi(
          event.poi,
          false,
          this.bookingParameters.partner.replace('partner-', 'partner-/')
        );
      } else {
        this.navigationService.navigateToPoi(event.poi);
      }
    }
    this.townService.getTownForSlug(event.slug);
  }

  openMapForShop() {
    return (shop: ShopListInterface) => {
      this.map.nativeElement.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        this.mapService.changedPoint(shop.location);
      }, 300);
    };
  }

  /**
   * set page title
   */
  private updateTitle() {
    if (this.town) {
      this.title.setTitle(
        this.translateService.instant('POI.title', {
          town_name: this.town.seoNames[this.translateService.currentLang],
          country_name: this.town.countryNames[this.translateService.currentLang]
        })
      );
    }
  }

  /**
   * Scolls the shop in the list selected on the map.
   * @param shop
   */
  protected scrollToSelectedShop(shop: ShopListInterface) {
    setTimeout(() => {
      this.scrollService.scrollIntoView(document.getElementById('scroll-shop-' + shop.id));
    }, 100);
  }

  private supportInteractiveMode(): boolean {
    return this.bookingParameters.userDefinedSearch && this.resolutionService.isLargeDevice();
  }

  private onErrorUpdate() {
    this.loading = false;
    this.error = true;
  }

  private onDisplayingShop(shop: ShopListInterface) {
    this.scrollToSelectedShop(shop);
  }

  private onSaleslocationNotFoundUpdate(townNotFound: TownNotFoundInterface) {
    this.responseService.setResponseCode(404);
    this.error = true;
    this.loading = false;
    this.saleslocationNotFound = townNotFound;
  }

  /**
   * get infos for language changes, update services and texts that will not change automatically
   * overwrites base list function for lang change subscription
   */
  private onLanguageChange(lang: string) {
    this.culture = lang;
    this.updateTitle();
  }

  private onGoToProduct(shop: Shop) {
    if (this.bookingParameters.userDefinedSearch) {
      // send user to product selection
      const route = this.localizedRouterService.translateRoute('/SKIHIREENTRY/SKIHIRE.PRODUCTS');
      const routeWithPartner = this.partnerLinkService.getUrlReplacingPartnerAfterPrefix(
        route,
        this.localizedRouterService.translateRoute('/SKIHIREENTRY')
      );
      try {
        this.window.history.scrollRestoration = 'auto';
      } catch {
        /* empty */
      }
      const queryParams = { shopId: shop.id };

      this.window.location.href = this.router
        .createUrlTree([routeWithPartner], {
          queryParams: queryParams
        })
        .toString();
    } else {
      // send user back to form
      this.shopSearchHelperService.lastSelectedShop = shop;
      this.search.setFocus({ behavior: 'smooth' });
    }
  }

  private createAnalyticsEvent(eventCategory: string, eventAction: string) {
    this.analyticsService.sendData('send', {
      hitType: 'event',
      eventCategory: eventCategory,
      eventAction: eventAction,
      eventLabel: ''
    });
  }
}
